import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Phone, Send, ShieldAlert, MessageCircle, RefreshCw, CheckCircle2,
         XCircle, Clock, Inbox } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/common";

const STATUS_STYLES = {
  sent:      "bg-emerald-100 text-emerald-800",
  delivered: "bg-blue-100 text-blue-800",
  read:      "bg-purple-100 text-purple-800",
  failed:    "bg-rose-100 text-rose-800",
  cancelled: "bg-amber-100 text-amber-800",
  pending:   "bg-slate-100 text-slate-700",
};

function StatusPill({ status, testid }) {
  const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-700";
  return (
    <span data-testid={testid}
          className={"inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold " + cls}>
      {status || "-"}
    </span>
  );
}

function BoolBadge({ ok }) {
  return ok
    ? <CheckCircle2 className="inline h-4 w-4 text-emerald-600" />
    : <XCircle className="inline h-4 w-4 text-slate-300" />;
}

export default function WhatsAppTestCentrePage() {
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [selectedTo, setSelectedTo] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateVars, setTemplateVars] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      setRefreshing(true);
      const [c, t, l, i, m] = await Promise.all([
        api.get("/admin/whatsapp/test-centre/config"),
        api.get("/admin/whatsapp/test-centre/templates").catch(() => ({ data: { templates: [] } })),
        api.get("/admin/whatsapp/test-centre/logs?limit=50"),
        api.get("/admin/whatsapp/test-centre/incoming?limit=30"),
        api.get("/admin/whatsapp/test-centre/matrix"),
      ]);
      setConfig(c.data);
      setTemplates(t.data?.templates || []);
      setLogs(l.data || []);
      setIncoming(i.data || []);
      setMatrix(m.data?.matrix || []);
      if (!selectedTo && c.data?.allowlist?.length) {
        setSelectedTo(c.data.allowlist[0].digits);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {                    // Auto-refresh every 15s to catch webhook updates
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const sendHello = async (digits) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/whatsapp/test-centre/send-hello", { to: digits });
      toast.success(`hello_world sent → ${r.data.recipient} (mid …${(r.data.provider_message_id||"").slice(-12)})`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const sendTemplate = async () => {
    if (!selectedTo) return toast.error("Pick a recipient first");
    if (!selectedTemplate) return toast.error("Pick a template first");
    const vars = templateVars.trim()
      ? templateVars.split(",").map((s) => s.trim())
      : [];
    setBusy(true);
    try {
      const tpl = templates.find((t) => t.name === selectedTemplate);
      const r = await api.post("/admin/whatsapp/test-centre/send-template", {
        to: selectedTo,
        template_name: selectedTemplate,
        language_code: tpl?.language || "en_US",
        variables: vars,
      });
      toast.success(`Template sent → ${r.data.recipient}`);
      setTemplateVars("");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const templateNeedsVars = useMemo(() => {
    const tpl = templates.find((t) => t.name === selectedTemplate);
    if (!tpl) return 0;
    // Count {{n}} placeholders across BODY component
    const body = (tpl.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
    if (!body?.text) return 0;
    return (body.text.match(/\{\{\d+\}\}/g) || []).length;
  }, [selectedTemplate, templates]);

  if (!config) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="theme-v2 p-4 md:p-6 space-y-6" data-testid="whatsapp-test-centre">
      {/* ============ Header ============ */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">WhatsApp Cloud API — Test Centre</h1>
          <p className="text-sm text-slate-600 mt-1">
            Super Admin → Communications → WhatsApp Cloud API → Test Centre
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={refreshing} data-testid="refresh-btn">
          <RefreshCw className={"h-4 w-4 mr-1 " + (refreshing ? "animate-spin" : "")} />
          Refresh
        </Button>
      </div>

      {/* ============ Safety banner ============ */}
      <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
           data-testid="test-mode-banner">
        <ShieldAlert className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-bold text-amber-900 tracking-wide">
            {config.test_mode ? "TEST MODE — Only authorised test recipients can receive WhatsApp messages"
                              : "⚠️ TEST MODE OFF — Real notifications would be sent"}
          </p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            Test mode: <b>{config.test_mode ? "true" : "false"}</b> · Notifications enabled: <b>{config.notifications_enabled ? "true" : "false"}</b> · API enabled: <b>{config.api_enabled ? "true" : "false"}</b>
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Sender: <b>{config.sender_display}</b> · Phone ID: <code className="bg-white px-1 rounded">{config.sender_phone_number_id}</code> · Graph: {config.graph_api_version}
          </p>
        </div>
      </div>

      {/* ============ Recipient selector + hello_world buttons ============ */}
      <section className="rounded-xl border bg-white p-4 md:p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <Phone className="h-4 w-4" /> Authorised test recipients ({config.allowlist.length})
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          Backend rejects any recipient outside this allowlist with <code className="bg-slate-100 px-1 rounded">BLOCKED — RECIPIENT NOT AUTHORISED FOR TESTING</code>. Configured in <code>WHATSAPP_TEST_RECIPIENTS</code>.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          {config.allowlist.map((r) => (
            <label key={r.digits}
                   data-testid={`recipient-card-${r.digits}`}
                   className={"rounded-lg border p-3 cursor-pointer flex items-center gap-3 transition-colors " +
                     (selectedTo === r.digits ? "border-purple-400 bg-purple-50" : "border-slate-200 hover:bg-slate-50")}>
              <input type="radio" checked={selectedTo === r.digits}
                     onChange={() => setSelectedTo(r.digits)}
                     className="h-4 w-4 accent-purple-700" />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm font-semibold">{r.display}</p>
                <p className="text-[11px] text-slate-500">{r.digits}</p>
              </div>
              <Button size="sm" onClick={(e) => { e.preventDefault(); sendHello(r.digits); }}
                      disabled={busy}
                      data-testid={`send-hello-${r.digits}`}
                      className="bg-purple-700 hover:bg-purple-800">
                <Send className="h-3.5 w-3.5 mr-1" /> hello_world
              </Button>
            </label>
          ))}
        </div>
      </section>

      {/* ============ Template picker ============ */}
      <section className="rounded-xl border bg-white p-4 md:p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <MessageCircle className="h-4 w-4" /> Approved templates ({templates.length})
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          Only <b>APPROVED</b> Meta templates appear here. Pick one → fill variables → send to the selected recipient.
        </p>
        <div className="grid md:grid-cols-[1fr_2fr] gap-3 items-start">
          <div>
            <label className="text-xs font-medium text-slate-600">Template</label>
            <select value={selectedTemplate}
                    onChange={(e) => { setSelectedTemplate(e.target.value); setTemplateVars(""); }}
                    className="w-full mt-1 rounded-md border px-3 py-2 text-sm bg-white"
                    data-testid="template-select">
              <option value="">— pick a template —</option>
              {templates.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name} ({t.language} · {t.category})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">
              Body variables (comma-separated, in order — {templateNeedsVars} required)
            </label>
            <Input value={templateVars} onChange={(e) => setTemplateVars(e.target.value)}
                   placeholder="e.g. TEST Parent, TEST Course, 2026-02-10"
                   className="mt-1"
                   data-testid="template-vars-input" />
            <p className="text-[11px] text-slate-500 mt-1">
              Tip: prefix free-form test values with <b>TEST</b> so recipients know it's not real.
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-slate-500">
            Sending to: <b>{config.allowlist.find((r) => r.digits === selectedTo)?.display || "—"}</b>
          </p>
          <Button onClick={sendTemplate} disabled={busy || !selectedTemplate || !selectedTo}
                  className="bg-purple-700 hover:bg-purple-800"
                  data-testid="send-template-btn">
            <Send className="h-4 w-4 mr-1" /> Send template
          </Button>
        </div>
      </section>

      {/* ============ PASS / FAIL matrix ============ */}
      <section className="rounded-xl border bg-white p-4 md:p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Test Matrix</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="matrix-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left p-2">Recipient</th>
                <th className="p-2">API accepted</th>
                <th className="p-2">Sent</th>
                <th className="p-2">Delivered</th>
                <th className="p-2">Read</th>
                <th className="p-2">Failed</th>
                <th className="p-2">Reply received</th>
                <th className="p-2">Auto-redirect</th>
                <th className="p-2">Dedupe OK</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((m) => (
                <tr key={m.recipient_digits} data-testid={`matrix-row-${m.recipient_digits}`}
                    className="border-t">
                  <td className="p-2 font-mono text-xs">{m.recipient_display}</td>
                  <td className="p-2 text-center"><BoolBadge ok={m.api_accepted} /></td>
                  <td className="p-2 text-center"><BoolBadge ok={m.sent_webhook} /></td>
                  <td className="p-2 text-center"><BoolBadge ok={m.delivered_webhook} /></td>
                  <td className="p-2 text-center"><BoolBadge ok={m.read_webhook} /></td>
                  <td className="p-2 text-center">
                    {m.failed_webhook ? <XCircle className="inline h-4 w-4 text-rose-600" /> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-2 text-center">
                    <BoolBadge ok={m.incoming_reply_received} />
                    {m.incoming_count > 0 && <span className="ml-1 text-[10px] text-slate-500">×{m.incoming_count}</span>}
                  </td>
                  <td className="p-2 text-center">
                    <BoolBadge ok={m.auto_redirect_sent} />
                    {m.auto_redirect_count > 1 && <span className="ml-1 text-[10px] text-rose-600 font-bold">×{m.auto_redirect_count}</span>}
                  </td>
                  <td className="p-2 text-center"><BoolBadge ok={m.duplicate_prevention_ok} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ============ Outbound sends log ============ */}
      <section className="rounded-xl border bg-white p-4 md:p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4" /> Recent outbound sends
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="logs-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Recipient</th>
                <th className="text-left p-2">Kind</th>
                <th className="text-left p-2">Template</th>
                <th className="p-2">API</th>
                <th className="p-2">Webhook</th>
                <th className="text-left p-2">Last event</th>
                <th className="text-left p-2">Meta message ID</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan="8" className="p-4 text-center text-slate-400 italic">No sends yet</td></tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className="border-t" data-testid={`log-row-${l.id}`}>
                  <td className="p-2 text-xs text-slate-500 whitespace-nowrap">
                    {(l.created_at || "").slice(11, 19)}
                  </td>
                  <td className="p-2 font-mono text-xs">{l.recipient_display}</td>
                  <td className="p-2 text-xs">
                    {l.kind === "auto_redirect"
                      ? <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-semibold">AUTO-REDIRECT</span>
                      : l.kind}
                  </td>
                  <td className="p-2 text-xs">{l.template_name || "-"}</td>
                  <td className="p-2 text-center"><StatusPill status={l.status} /></td>
                  <td className="p-2 text-center"><StatusPill status={l.webhook_status || "-"} /></td>
                  <td className="p-2 text-xs text-slate-500">
                    {l.last_provider_event || "-"}
                    {l.webhook_error && <span className="block text-rose-600 text-[10px]">{l.webhook_error}</span>}
                    {l.error && <span className="block text-amber-700 text-[10px]">{l.error}</span>}
                  </td>
                  <td className="p-2 font-mono text-[10px] text-slate-400">
                    …{(l.provider_message_id || "").slice(-16)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ============ Inbound replies ============ */}
      <section className="rounded-xl border bg-white p-4 md:p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Inbox className="h-4 w-4" /> Recent inbound replies
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="incoming-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">From</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Message</th>
                <th className="text-left p-2">Meta message ID</th>
              </tr>
            </thead>
            <tbody>
              {incoming.length === 0 && (
                <tr><td colSpan="5" className="p-4 text-center text-slate-400 italic">No inbound messages yet — ask recipients to reply <b>Hi</b></td></tr>
              )}
              {incoming.map((m) => (
                <tr key={m.id} className="border-t" data-testid={`incoming-row-${m.id}`}>
                  <td className="p-2 text-xs text-slate-500 whitespace-nowrap">
                    {(m.received_at || "").slice(11, 19)}
                  </td>
                  <td className="p-2 font-mono text-xs">{m.from_digits || m.from_masked || "-"}</td>
                  <td className="p-2 text-xs">{m.type}</td>
                  <td className="p-2 text-xs max-w-md truncate">{m.body || <span className="text-slate-400">(no body)</span>}</td>
                  <td className="p-2 font-mono text-[10px] text-slate-400">
                    …{(m.provider_message_id || "").slice(-16)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
